import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import * as api from '@/lib/api'
import type { AdminRelease, AdminGroup } from '@/lib/api'
import AssetImageField from '@/components/AssetImageField'

interface FormData {
  group_slug: string
  image_url: string
  alt: string
  spotify_url: string
  label: string
  title: string
  release_date: string
  tracks: string
  more_tracks: string
}

const blank: FormData = {
  group_slug: '', image_url: '', alt: '', spotify_url: '', label: '', title: '',
  release_date: '', tracks: '', more_tracks: '',
}

function toFormData(r: AdminRelease): FormData {
  return {
    group_slug: r.group_slug, image_url: r.image_url, alt: r.alt,
    spotify_url: r.spotify_url, label: r.label, title: r.title,
    release_date: r.release_date, tracks: r.tracks.join('\n'),
    more_tracks: r.more_tracks?.toString() ?? '',
  }
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900 overflow-hidden">
      <div className="px-5 py-3 border-b border-zinc-800">
        <h2 className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">{title}</h2>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  )
}

export default function DiscographyFormPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { token, isGroupManager, managedGroupSlug } = useAuth()

  const isEdit = Boolean(id)

  const [form, setForm] = useState<FormData>(blank)
  const [groups, setGroups] = useState<AdminGroup[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!token) return

    const fetchGroups = api.getAdminGroups(token)

    if (isEdit) {
      const stateRelease = (location.state as { release?: AdminRelease } | null)?.release
      if (stateRelease) {
        fetchGroups.then((groupList) => {
          setGroups(groupList)
          setForm(toFormData(stateRelease))
          setLoading(false)
        })
      } else {
        Promise.all([fetchGroups, api.getAdminDiscography(token)]).then(([groupList, releases]) => {
          setGroups(groupList)
          const found = releases.find((r) => r.id === id)
          if (found) setForm(toFormData(found))
          setLoading(false)
        })
      }
    } else {
      const stateGroupSlug = (location.state as { initialGroupSlug?: string } | null)?.initialGroupSlug
      fetchGroups.then((groupList) => {
        setGroups(groupList)
        const slug = (isGroupManager && managedGroupSlug) ? managedGroupSlug : (stateGroupSlug ?? '')
        if (slug) setForm((prev) => ({ ...prev, group_slug: slug }))
        setLoading(false)
      })
    }
  }, [id, isEdit, token, location.state, isGroupManager, managedGroupSlug])

  const set = (field: keyof FormData, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!token) return
    setSaving(true)
    try {
      const payload = {
        group_slug: form.group_slug,
        image_url: form.image_url,
        alt: form.alt,
        spotify_url: form.spotify_url,
        label: form.label,
        title: form.title,
        release_date: form.release_date,
        tracks: form.tracks.split('\n').map((t) => t.trim()).filter(Boolean),
        more_tracks: form.more_tracks ? parseInt(form.more_tracks) : null,
      }

      if (isEdit && id) {
        const updated = await api.updateDiscography(token, id, payload)
        navigate(`/discography/${id}`, { replace: true, state: { release: updated } })
      } else {
        await api.createDiscography(token, payload)
        navigate(-1)
      }
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  const inp = 'w-full bg-zinc-800 text-white rounded-lg px-3 py-2 text-sm border border-zinc-700 focus:outline-none focus:border-zinc-500 placeholder:text-zinc-600'
  const lbl = 'block text-xs text-zinc-400 mb-1.5'

  if (loading) {
    return <div className="p-6"><p className="text-zinc-500 text-sm">Loading…</p></div>
  }

  return (
    <div className="p-6">
      <form onSubmit={handleSubmit}>
        {/* Sticky top bar */}
        <div className="sticky top-0 z-10 bg-zinc-950 flex items-center justify-between pb-4 mb-6 border-b border-zinc-800">
          <div>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300 transition-colors mb-3"
            >
              <ChevronLeft size={14} />
              Kembali
            </button>
            <h1 className="text-xl font-bold text-white">
              {isEdit ? 'Edit Release' : 'New Release'}
            </h1>
            {isEdit && <p className="text-zinc-500 text-sm mt-1">{form.title}</p>}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-5 py-2 text-sm text-zinc-400 hover:text-white transition-colors rounded-lg hover:bg-zinc-800"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 text-sm font-semibold bg-yellow-500 hover:bg-yellow-400 text-black rounded-lg transition-colors disabled:opacity-50"
            >
              {saving ? 'Menyimpan…' : isEdit ? 'Simpan Perubahan' : 'Buat Release'}
            </button>
          </div>
        </div>
        <div className="max-w-3xl mx-auto space-y-4">

        {/* ── Informasi Rilis ──────────────────────────── */}
        <Section title="Informasi Rilis">
          <div>
            <label className={lbl}>Judul <span className="text-red-400">*</span></label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              required
              placeholder="Judul album / single"
              className={inp}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Group <span className="text-red-400">*</span></label>
              {isGroupManager ? (
                <input
                  type="text"
                  value={form.group_slug}
                  disabled
                  className={inp + ' opacity-40 cursor-not-allowed'}
                />
              ) : (
                <select
                  value={form.group_slug}
                  onChange={(e) => set('group_slug', e.target.value)}
                  required
                  className={inp}
                >
                  <option value="">Pilih group…</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.slug}>{g.name}</option>
                  ))}
                </select>
              )}
            </div>
            <div>
              <label className={lbl}>
                Label Rilis <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={form.label}
                onChange={(e) => set('label', e.target.value)}
                required
                placeholder="contoh: 7th SINGLE, 2nd MINI ALBUM"
                className={inp}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Tanggal Rilis</label>
              <input
                type="date"
                value={form.release_date}
                onChange={(e) => set('release_date', e.target.value)}
                className={inp + ' [color-scheme:dark]'}
              />
            </div>
            <div>
              <label className={lbl}>Spotify URL</label>
              <input
                type="url"
                value={form.spotify_url}
                onChange={(e) => set('spotify_url', e.target.value)}
                placeholder="https://open.spotify.com/album/..."
                className={inp}
              />
            </div>
          </div>
        </Section>

        {/* ── Cover Art ───────────────────────────────── */}
        <Section title="Cover Art">
          <div className="grid grid-cols-3 gap-5">
            <div className="col-span-2">
              <label className={lbl}>Gambar Cover</label>
              <AssetImageField
                value={form.image_url}
                onChange={(url) => set('image_url', url)}
                label="Cover Art"
                feature="discography"
              />
            </div>
            <div>
              <label className={lbl}>Alt Text</label>
              <textarea
                value={form.alt}
                onChange={(e) => set('alt', e.target.value)}
                rows={3}
                placeholder="Deskripsi gambar untuk aksesibilitas"
                className={inp + ' resize-none h-[88px]'}
              />
            </div>
          </div>
        </Section>

        {/* ── Track List ──────────────────────────────── */}
        <Section title="Track List">
          <div>
            <label className={lbl}>Daftar Track</label>
            <textarea
              value={form.tracks}
              onChange={(e) => set('tracks', e.target.value)}
              rows={6}
              placeholder={'Track 1\nTrack 2\nTrack 3'}
              className={inp + ' resize-y'}
            />
            <p className="text-[11px] text-zinc-600 mt-1.5">Satu track per baris</p>
          </div>

          <div className="w-48">
            <label className={lbl}>
              Jumlah Track Tambahan
            </label>
            <input
              type="number"
              value={form.more_tracks}
              onChange={(e) => set('more_tracks', e.target.value)}
              min="0"
              placeholder="0"
              className={inp}
            />
            <p className="text-[11px] text-zinc-600 mt-1.5">
              Track yang ada di album tapi tidak tercantum di atas
            </p>
          </div>
        </Section>

        </div>
      </form>
    </div>
  )
}
